import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Lazy load components to reduce bundle size
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Tenants = lazy(() => import('./pages/Tenants'));
const Users = lazy(() => import('./pages/Users'));
const Billing = lazy(() => import('./pages/Billing'));
const InvoiceSettings = lazy(() => import('./pages/InvoiceSettings'));
const SystemSettings = lazy(() => import('./pages/SystemSettings'));
const CompanySettings = lazy(() => import('./pages/CompanySettings'));
const ShopifySettings = lazy(() => import('./pages/ShopifySettings'));
const WhatsAppBot = lazy(() => import('./pages/WhatsAppBot'));
const WhatsAppBots = lazy(() => import('./pages/WhatsAppBots'));
const TelegramBots = lazy(() => import('./pages/TelegramBots'));
const BackupRestore = lazy(() => import('./pages/BackupRestore'));
const AuditTrail = lazy(() => import('./pages/AuditTrail'));
const APIKeys = lazy(() => import('./pages/APIKeys'));
const Webhooks = lazy(() => import('./pages/Webhooks'));
const StockShare = lazy(() => import('./pages/StockShare'));
const PublicStockView = lazy(() => import('./pages/PublicStockView'));
const EmailSettings = lazy(() => import('./pages/EmailSettings'));
const Returns = lazy(() => import('./pages/Returns'));
const InventoryLedger = lazy(() => import('./pages/InventoryLedger'));
const Automation = lazy(() => import('./pages/Automation'));
const AIAnalytics = lazy(() => import('./pages/AIAnalytics'));
const WisoSettings = lazy(() => import('./pages/WisoSettings'));
const PackingStation = lazy(() => import('./pages/PackingStation'));
const BatchPicking = lazy(() => import('./pages/BatchPicking'));
const BoxManagement = lazy(() => import('./pages/BoxManagement'));
const MultiPackStation = lazy(() => import('./pages/MultiPackStation'));
const PackMonitor = lazy(() => import('./pages/PackMonitor'));
const PicklistPrint = lazy(() => import('./pages/PicklistPrint'));
const LedConfig = lazy(() => import('./pages/LedConfig'));
const PickToLightGuide = lazy(() => import('./pages/PickToLightGuide'));
const UpsDebug = lazy(() => import('./pages/UpsDebug'));
const Shipments = lazy(() => import('./pages/Shipments'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const FeatureOverview = lazy(() => import('./pages/FeatureOverview'));
const Articles = lazy(() => import('./pages/Articles'));
const Stock = lazy(() => import('./pages/Stock'));
const StockHistory = lazy(() => import('./pages/StockHistory'));
const LabelGenerator = lazy(() => import('./pages/LabelGenerator'));
const StorageLocations = lazy(() => import('./pages/StorageLocations'));
const GoodsReceipt = lazy(() => import('./pages/GoodsReceipt'));
const Recipients = lazy(() => import('./pages/Recipients'));
const Orders = lazy(() => import('./pages/Orders'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));
const ShippingSettings = lazy(() => import('./pages/ShippingSettings'));
const WarehouseLayout = lazy(() => import('./pages/WarehouseLayout'));
const Layout = lazy(() => import('./components/Layout'));

// Mobile components
const MobileLogin = lazy(() => import('./pages/mobile/MobileLogin'));
const MobileDashboard = lazy(() => import('./pages/mobile/MobileDashboard'));
const MobileGoodsReceipt = lazy(() => import('./pages/mobile/MobileGoodsReceipt'));
const MobileArticleInfo = lazy(() => import('./pages/mobile/MobileArticleInfo'));
const MobileOrders = lazy(() => import('./pages/mobile/MobileOrders'));
const MobileScan = lazy(() => import('./pages/mobile/MobileScan'));
const MobilePicking = lazy(() => import('./pages/mobile/MobilePicking'));
const MobilePickPack = lazy(() => import('./pages/mobile/MobilePickPack'));
const MobileInventory = lazy(() => import('./pages/mobile/MobileInventory'));
const MobileRelocation = lazy(() => import('./pages/mobile/MobileRelocation'));

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  return user ? children : <Navigate to="/login" replace />;
};

const MobilePrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  return user ? children : <Navigate to="/mobile/login" replace />;
};

const Loading = () => <div className="flex items-center justify-center min-h-screen"><div>Laden...</div></div>;

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route 
              path="/" 
              element={
                <PrivateRoute>
                  <Layout><Dashboard /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/articles" 
              element={
                <PrivateRoute>
                  <Layout><Articles /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/stock" 
              element={
                <PrivateRoute>
                  <Layout><Stock /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/stock-history" 
              element={
                <PrivateRoute>
                  <Layout><StockHistory /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/label-generator" 
              element={
                <PrivateRoute>
                  <Layout><LabelGenerator /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/locations" 
              element={
                <PrivateRoute>
                  <Layout><StorageLocations /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/goods-receipt" 
              element={
                <PrivateRoute>
                  <Layout><GoodsReceipt /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/recipients" 
              element={
                <PrivateRoute>
                  <Layout><Recipients /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/orders" 
              element={
                <PrivateRoute>
                  <Layout><Orders /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/purchase-orders" 
              element={
                <PrivateRoute>
                  <Layout><PurchaseOrders /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/shipping-settings" 
              element={
                <PrivateRoute>
                  <Layout><ShippingSettings /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/warehouse-layout" 
              element={
                <PrivateRoute>
                  <Layout><WarehouseLayout /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/tenants" 
              element={
                <PrivateRoute>
                  <Layout><Tenants /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/users" 
              element={
                <PrivateRoute>
                  <Layout><Users /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/billing" 
              element={
                <PrivateRoute>
                  <Layout><Billing /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/invoice-settings" 
              element={
                <PrivateRoute>
                  <Layout><InvoiceSettings /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/system-settings" 
              element={
                <PrivateRoute>
                  <Layout><SystemSettings /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/company-settings" 
              element={
                <PrivateRoute>
                  <Layout><CompanySettings /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/shopify-settings" 
              element={
                <PrivateRoute>
                  <Layout><ShopifySettings /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/api-keys" 
              element={
                <PrivateRoute>
                  <Layout><APIKeys /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/webhooks" 
              element={
                <PrivateRoute>
                  <Layout><Webhooks /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/stock-share" 
              element={
                <PrivateRoute>
                  <Layout><StockShare /></Layout>
                </PrivateRoute>
              } 
            />
            {/* Public route - no auth */}
            <Route path="/stock-view/:token" element={<Suspense fallback={<Loading />}><PublicStockView /></Suspense>} />
            <Route path="/landing" element={<Suspense fallback={<Loading />}><LandingPage /></Suspense>} />
            <Route path="/features" element={<Suspense fallback={<Loading />}><FeatureOverview /></Suspense>} />
            <Route 
              path="/email-settings" 
              element={
                <PrivateRoute>
                  <Layout><EmailSettings /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/returns" 
              element={
                <PrivateRoute>
                  <Layout><Returns /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/inventory-ledger" 
              element={
                <PrivateRoute>
                  <Layout><InventoryLedger /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/automation" 
              element={
                <PrivateRoute>
                  <Layout><Automation /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/ai-analytics" 
              element={
                <PrivateRoute>
                  <Layout><AIAnalytics /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/wiso-settings" 
              element={
                <PrivateRoute>
                  <Layout><WisoSettings /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/packing-station" 
              element={
                <PrivateRoute>
                  <Layout><PackingStation /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/batch-picking" 
              element={
                <PrivateRoute>
                  <Layout><BatchPicking /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/box-management" 
              element={
                <PrivateRoute>
                  <Layout><BoxManagement /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/multi-pack" 
              element={
                <PrivateRoute>
                  <Layout><MultiPackStation /></Layout>
                </PrivateRoute>
              } 
            />
            <Route path="/pack-monitor" element={<Suspense fallback={<Loading />}><PackMonitor /></Suspense>} />
            <Route 
              path="/picklist" 
              element={
                <PrivateRoute>
                  <Layout><PicklistPrint /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/led-config" 
              element={
                <PrivateRoute>
                  <Layout><LedConfig /></Layout>
                </PrivateRoute>
              } 
            />
            <Route path="/ptl-guide" element={<Suspense fallback={<Loading />}><PickToLightGuide /></Suspense>} />
            <Route 
              path="/ups-debug" 
              element={
                <PrivateRoute>
                  <Layout><UpsDebug /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/shipments" 
              element={
                <PrivateRoute>
                  <Layout><Shipments /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/whatsapp-bot" 
              element={
                <PrivateRoute>
                  <Layout><WhatsAppBot /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/whatsapp-bots" 
              element={
                <PrivateRoute>
                  <Layout><WhatsAppBots /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/telegram-bots" 
              element={
                <PrivateRoute>
                  <Layout><TelegramBots /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/backup" 
              element={
                <PrivateRoute>
                  <Layout><BackupRestore /></Layout>
                </PrivateRoute>
              } 
            />
            <Route 
              path="/audit-trail" 
              element={
                <PrivateRoute>
                  <Layout><AuditTrail /></Layout>
                </PrivateRoute>
              } 
            />
            
            {/* Mobile Routes */}
            <Route path="/mobile" element={<Navigate to="/mobile/login" replace />} />
            <Route path="/mobile/login" element={<MobileLogin />} />
            <Route 
              path="/mobile/dashboard" 
              element={
                <MobilePrivateRoute>
                  <MobileDashboard />
                </MobilePrivateRoute>
              } 
            />
            <Route 
              path="/mobile/goods-receipt" 
              element={
                <MobilePrivateRoute>
                  <MobileGoodsReceipt />
                </MobilePrivateRoute>
              } 
            />
            <Route 
              path="/mobile/article-info" 
              element={
                <MobilePrivateRoute>
                  <MobileArticleInfo />
                </MobilePrivateRoute>
              } 
            />
            <Route 
              path="/mobile/orders" 
              element={
                <MobilePrivateRoute>
                  <MobileOrders />
                </MobilePrivateRoute>
              } 
            />
            <Route 
              path="/mobile/scan" 
              element={
                <MobilePrivateRoute>
                  <MobileScan />
                </MobilePrivateRoute>
              } 
            />
            <Route 
              path="/mobile/picking" 
              element={
                <MobilePrivateRoute>
                  <MobilePicking />
                </MobilePrivateRoute>
              } 
            />
            <Route 
              path="/mobile/picking/:orderId" 
              element={
                <MobilePrivateRoute>
                  <MobilePicking />
                </MobilePrivateRoute>
              } 
            />
            <Route 
              path="/mobile/pick-pack" 
              element={
                <MobilePrivateRoute>
                  <MobilePickPack />
                </MobilePrivateRoute>
              } 
            />
            <Route 
              path="/mobile/inventory" 
              element={
                <MobilePrivateRoute>
                  <MobileInventory />
                </MobilePrivateRoute>
              } 
            />
            <Route 
              path="/mobile/relocation" 
              element={
                <MobilePrivateRoute>
                  <MobileRelocation />
                </MobilePrivateRoute>
              } 
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
